/**
 * Compact comment placeholder: avatar, byline bar, and two body lines.
 *
 * `SkeletonCommentInCardStack` is the same placeholder framed by `CardStack`,
 * for teaser surfaces — where it doubles as the standing empty-conversation
 * visual, so callers there pass `isLoading={false}`.
 */

import { Pad, PadBox } from 'component/basics';
import { CardStack } from 'component/card-stack';
import type { DimensionValue } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { BaseSkeleton } from './base-skeleton';
import { TextContainerSkeleton } from './text-container-skeleton';

export function SkeletonCommentInCardStack({ width, isLoading }: { width?: DimensionValue; isLoading?: boolean }) {
    return (
        <CardStack width={width}>
            <PadBox horiz={12} vert={12}>
                <SkeletonComment isLoading={isLoading} />
            </PadBox>
        </CardStack>
    );
}

export type SkeletonCommentProps = { isLoading?: boolean };

export function SkeletonComment({ isLoading }: SkeletonCommentProps) {
    const s = SkeletonCommentStyle;
    return (
        <View>
            <View style={s.horiz}>
                <SkeletonProfilePicture isLoading={isLoading} />
                <TextContainerSkeleton borderRadius={4} height={7} isLoading={isLoading} width={76} />
            </View>
            <View>
                <Pad size={16} />
                <View>
                    <TextContainerSkeleton isLoading={isLoading} width='95%' />
                    <Pad size={6} />
                    <TextContainerSkeleton isLoading={isLoading} width='70%' />
                </View>
            </View>
            <Pad size={18} />
        </View>
    );
}

const SkeletonCommentStyle = StyleSheet.create({
    horiz: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});

function SkeletonProfilePicture({ isLoading }: { isLoading?: boolean }) {
    return <BaseSkeleton borderRadius={16} height={24} isLoading={isLoading} marginRight={8} width={24} />;
}
